import { useMemo, useState } from 'react'
import { Add, Remove } from '@mui/icons-material'
import { Box, Stack, Typography } from '@mui/material'
import { RosterProfileQuery } from '__generated__/graphql'
import { useCandidateSearchUrlState } from 'shared/hooks/candidateSearchUrlState'
import { LabelTypography } from '../styled'

type StatusDataItem = NonNullable<
    NonNullable<RosterProfileQuery['roster']>['candidateProcessStatusSummary']
>[number]

interface StatusGroup {
    groupId: string
    groupLabel: string
    items: StatusDataItem[]
}

interface CandidatePipelineProps {
    roster: RosterProfileQuery['roster']
}

export const CandidatePipeline = ({ roster }: CandidatePipelineProps) => {
    const [expandedGroup, setExpandedGroup] = useState<string | null>(null)
    const { filters, setFilters } = useCandidateSearchUrlState()

    const groupedData = useMemo(() => {
        try {
            const statusData = roster?.candidateProcessStatusSummary
            if (!statusData) {
                return {}
            }

            // Group by status group
            const grouped = statusData.reduce<Record<string, StatusGroup>>(
                (acc, item) => {
                    const groupLabel = item.statusSummaryGroup.label
                    if (!acc[groupLabel]) {
                        acc[groupLabel] = {
                            groupId: item.statusSummaryGroup.id,
                            items: [],
                            groupLabel: groupLabel,
                        }
                    }
                    acc[groupLabel].items.push(item)
                    return acc
                },
                {}
            )

            return grouped
        } catch (error) {
            console.error('Error processing status data:', error)
            return {}
        }
    }, [roster])

    // Calculate total count
    const totalCount = useMemo(() => {
        return Object.values(groupedData).reduce((total, group) => {
            return (
                total + group.items.reduce((sum, item) => sum + item.count, 0)
            )
        }, 0)
    }, [groupedData])

    const handleChipClick = (groupLabel: string) => {
        setExpandedGroup(expandedGroup === groupLabel ? null : groupLabel)
    }

    const handleFilterToggle = (groupId: string) => {
        const current = filters.processStatusGroupIds
        const isActive = current.includes(groupId)
        setFilters({
            ...filters,
            processStatusGroupIds: isActive
                ? current.filter((id) => id !== groupId)
                : [...current, groupId],
        })
    }

    return (
        <Stack
            direction="column"
            alignItems="stretch"
            sx={{ width: 'fit-content' }}
        >
            {/* Header row */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 0.5,
                }}
            >
                <LabelTypography variant="overline">
                    Candidate Pipeline
                </LabelTypography>
                <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                    TOTAL{' '}
                    <Box component="span" sx={{ fontWeight: 700 }}>
                        {totalCount}
                    </Box>
                </Typography>
            </Box>

            {/* Status chips */}
            <Stack
                direction="row"
                sx={{
                    mb: 2,
                    flexWrap: 'wrap',
                    gap: 1,
                    maxWidth: 'fit-content',
                    flex: '0 0 auto',
                }}
            >
                {Object.entries(groupedData).map(([groupLabel, group]) => {
                    const groupCount = group.items.reduce(
                        (sum, item) => sum + item.count,
                        0
                    )
                    const isExpanded = expandedGroup === groupLabel
                    const isFiltered = filters.processStatusGroupIds.includes(
                        group.groupId
                    )

                    return (
                        <Box
                            key={groupLabel}
                            sx={{
                                display: 'flex',
                                alignItems: 'stretch',
                                borderRadius: '16px',
                                border: '1px solid',
                                borderColor: isExpanded
                                    ? 'primary.main'
                                    : 'divider',
                                bgcolor: isExpanded
                                    ? 'primary.main'
                                    : 'transparent',
                                overflow: 'hidden',
                                height: 'auto',
                            }}
                        >
                            <Box
                                onClick={() => handleChipClick(groupLabel)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    px: 1.25,
                                    py: 0.75,
                                    cursor: 'pointer',
                                    color: isExpanded
                                        ? 'primary.contrastText'
                                        : 'text.primary',
                                    '&:hover': {
                                        bgcolor: isExpanded
                                            ? 'primary.dark'
                                            : 'action.hover',
                                    },
                                }}
                            >
                                <Typography
                                    component="span"
                                    sx={{ fontSize: '0.8125rem' }}
                                >
                                    {groupLabel}
                                </Typography>
                                <Box
                                    sx={{
                                        backgroundColor: isExpanded
                                            ? 'primary.contrastText'
                                            : 'action.selected',
                                        color: isExpanded
                                            ? 'primary.main'
                                            : 'text.primary',
                                        borderRadius: '50%',
                                        width: 20,
                                        height: 20,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '0.625rem',
                                        fontWeight: 600,
                                    }}
                                >
                                    {groupCount}
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    width: '1px',
                                    bgcolor: isExpanded
                                        ? 'primary.light'
                                        : 'divider',
                                    alignSelf: 'stretch',
                                }}
                            />
                            <Box
                                onClick={() =>
                                    handleFilterToggle(group.groupId)
                                }
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    pl: 0.75,
                                    pr: 0.95,
                                    cursor: 'pointer',
                                    color: isFiltered
                                        ? isExpanded
                                            ? 'primary.contrastText'
                                            : 'primary.main'
                                        : isExpanded
                                          ? 'primary.contrastText'
                                          : 'text.secondary',
                                    bgcolor:
                                        isFiltered && !isExpanded
                                            ? 'primary.50'
                                            : 'transparent',
                                    '&:hover': {
                                        bgcolor: isExpanded
                                            ? 'primary.dark'
                                            : 'action.hover',
                                    },
                                }}
                            >
                                {isFiltered ? (
                                    <Remove sx={{ fontSize: '1rem' }} />
                                ) : (
                                    <Add sx={{ fontSize: '1rem' }} />
                                )}
                            </Box>
                        </Box>
                    )
                })}
            </Stack>

            {/* Expanded breakdown */}
            {expandedGroup && groupedData[expandedGroup] && (
                <Box>
                    <Typography
                        variant="body2"
                        sx={{
                            mb: 1.5,
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                            letterSpacing: '0.05em',
                        }}
                    >
                        {expandedGroup.toUpperCase()} BREAKDOWN
                    </Typography>
                    <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
                        {groupedData[expandedGroup].items.map((item) => (
                            <Box
                                key={item.id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'stretch',
                                    borderRadius: '16px',
                                    border: '1px solid',
                                    borderColor:
                                        filters.processStatusIds.includes(
                                            item.id
                                        )
                                            ? 'primary.main'
                                            : 'grey.300',
                                    bgcolor: filters.processStatusIds.includes(
                                        item.id
                                    )
                                        ? 'primary.50'
                                        : 'grey.50',
                                    overflow: 'hidden',
                                    height: 'auto',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        px: 1.25,
                                        py: 0.5,
                                        color: 'text.secondary',
                                        fontSize: '0.8125rem',
                                    }}
                                >
                                    <span>
                                        {item.statusLabel}{' '}
                                        <strong>{item.count}</strong>
                                    </span>
                                </Box>
                                <Box
                                    sx={{
                                        width: '1px',
                                        bgcolor:
                                            filters.processStatusIds.includes(
                                                item.id
                                            )
                                                ? 'primary.light'
                                                : 'grey.300',
                                        alignSelf: 'stretch',
                                    }}
                                />
                                <Box
                                    onClick={() => {
                                        const current = filters.processStatusIds
                                        const isActive = current.includes(
                                            item.id
                                        )
                                        setFilters({
                                            ...filters,
                                            processStatusIds: isActive
                                                ? current.filter(
                                                      (id) => id !== item.id
                                                  )
                                                : [...current, item.id],
                                        })
                                    }}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        pl: 0.75,
                                        pr: 0.95,
                                        cursor: 'pointer',
                                        color: filters.processStatusIds.includes(
                                            item.id
                                        )
                                            ? 'primary.main'
                                            : 'text.secondary',
                                        '&:hover': {
                                            bgcolor: 'action.hover',
                                        },
                                    }}
                                >
                                    {filters.processStatusIds.includes(
                                        item.id
                                    ) ? (
                                        <Remove sx={{ fontSize: '0.875rem' }} />
                                    ) : (
                                        <Add sx={{ fontSize: '0.875rem' }} />
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            )}
        </Stack>
    )
}
